package com.eladfinish.bible.contest.screens.search.usecases.specialSearch

import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.screens.browse.usecases.chumash.GetParashaContentUseCase
import com.eladfinish.bible.contest.screens.search.model.ChapterDetails
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.VersesStatisticsType.PER_ALIYOT
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.VersesStatisticsType.TOTAL_ASCENDING_ORDER
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.VersesStatisticsType.TOTAL_DESCENDING_ORDER
import com.eladfinish.bible.contest.utils.GetParashotUseCase
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier

/**
 * Developed by
 * @author Elad Finish
 */

enum class VersesStatisticsType {
    PER_ALIYOT,
    TOTAL_ASCENDING_ORDER,
    TOTAL_DESCENDING_ORDER,
}

private data class ParashaData(
    val name: String,
    val totalVerses: Int,
    val versesPerAliyot: List<Int>,
)

//statisticsOfAliyot
class VersesPerAliyot(
    private val getParashaContentUseCase: GetParashaContentUseCase,
    private val getParashotUseCase: GetParashotUseCase,
    private val settings: Settings,
    private val versesStatisticsType: VersesStatisticsType,
) : SpecialSearch {

    private val sb = StringBuilder()

    override fun process(chapterDetails: ChapterDetails) = Unit

    override fun isOnlyTorah(): Boolean = true

    override fun getSummary(): String {
        statisticsOfAliyot()
        return sb.toString()
    }

    private fun statisticsOfAliyot() {
        sb.append("דו\"ח מספר הפסוקים בפרשות התורה ")
        sb.append(
            when (versesStatisticsType) {
                PER_ALIYOT -> "בציון המספר בכל עלייה"
                TOTAL_ASCENDING_ORDER -> "בסדר עולה"
                TOTAL_DESCENDING_ORDER -> "בסדר יורד"
            }
        )
        sb.append("\n\n")
        sb.append("שם הפרשה | ")
        sb.append("סה\"כ פסוקים")
        if (versesStatisticsType == PER_ALIYOT) {
            sb.append(" | ")
            sb.append(MutableList(7) { it.plus(1) }.joinToString())
        }
        sb.append("\n")

        val parashaDataList = mutableListOf<ParashaData>()
        BooksRepository.chumashim.forEachIndexed { i, chumash ->
            val parashot = getParashotUseCase.parseParashot(bookNumber = i + 1)
            Napier.d("parashot: $parashot")
            if (versesStatisticsType == PER_ALIYOT) {
                sb.append("חומש $chumash:\n")
            }
            for (j in 0 until parashot.size) {
                val parasha = parashot.parashotNames[j]
                val source = parashot.parashotSources[j]
                val parashaContent = getParashaContentUseCase.invoke(
                    input = source,
                    bookIndex = i + 1,
                    onlyQri = settings.getBoolean(SharedPrefsConstants.ONLY_QRI_SWITCH, true),
                )
                val numberOfVerses = countMatches(parashaContent, StringConstants.COLON_SPLITTER)
                val connected = if (parasha.contains("-")) "2" else ""
                val aliyot = parashaContent.splitByRegex(regexString = "\\{$connected[1-7]\\}")
                val parashaData = ParashaData(
                    name = parasha,
                    totalVerses = numberOfVerses,
                    //drop first element since aliyot[0] is empty
                    versesPerAliyot = aliyot.drop(1).map { aliyah ->
                        countMatches(aliyah, StringConstants.COLON_SPLITTER)
                    },
                )
                parashaDataList += parashaData

                if (versesStatisticsType == PER_ALIYOT) {
                    sb.append(parashaData.name).append(" | ").append(parashaData.totalVerses).append(" | ")
                    sb.append(parashaData.versesPerAliyot.joinToString(separator = ", ", prefix = "", postfix = "\n"))
                }
            }
        }

        when (versesStatisticsType) {
            TOTAL_ASCENDING_ORDER -> parashaDataList.sortedBy { it.totalVerses }
            TOTAL_DESCENDING_ORDER -> parashaDataList.sortedByDescending { it.totalVerses }
            PER_ALIYOT -> null
        }?.forEachIndexed { index, parashaData ->
            sb.append(buildString {
                append(index.plus(1))
                append(". ")
                append(parashaData.name)
                append(" | ")
                append(parashaData.totalVerses)
                append("\n")
            })
        }
    }

    @Suppress("SameParameterValue")
    private fun countMatches(str: String, sub: String): Int {
        if (str.isEmpty() || sub.isEmpty()) return 0

        var count = 0
        var index = str.indexOf(sub)

        while (index != -1) {
            count++
            index = str.indexOf(
                string = sub,
                startIndex = index + sub.length,
            )
        }

        return count
    }

}
